import { useHttpErrorManager } from '@/composables/useHttpErrorManager';
import { useToast } from '@/composables/useToast';
import { PUBLIC_PEM } from '@/configs/publicKey.js';
import {
    clearAllIndexedDB,
    getOfflineSession,
    invalidateOfflineCacheWrites,
    setOfflineToken,
} from '@/indexedDB/offlineDB.js';
import { getConnectivityStatus as getRawConnectivityStatus } from '@/services/connectivityState.js';
import { onConnectivityStatusChange, setConnectivityStatus } from '@/services/onlineCheckService.js';
import {
    getOfflineCacheOwnerId,
    hasOfflineIdentityMismatch,
    publishOfflineSessionInvalidation,
    setOfflineCacheOwnerId,
    subscribeOfflineSessionInvalidations,
} from '@/services/offlineIdentity.js';
import { cancelOfflineListSyncs } from '@/services/offlineSyncService.js';
import { leaveOfflineSync } from '@/services/offlineRealtimeService.js';
import router from '@/router';
import apiClient, { clearApiGetCache } from '@/utils/axios';
import { importSPAPublicKey, verifyJWT } from '@/utils/jwt.js';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useAppearance } from '@/composables/useAppearance.js';
import { useSaleStore } from '@/stores/sale-store.js';
import { useHelpStore } from '@/stores/help-store.js';
import { useConfigurationStore } from '@/stores/configuration-store.js';

export const useAuthStore = defineStore('authStore', () => {
    // ----------------------------------
    // State
    // ----------------------------------
    const userData = ref(localStorage.getItem('userData') || null);
    const permissions = ref([]);
    const userDataFetched = ref(false);
    const isUpdatingCSRF = ref(false);
    const initialConnectivity = getRawConnectivityStatus();
    const isSystemOnline = ref(initialConnectivity === 'online');
    /** @type {import('vue').Ref<import('@/services/connectivityState.js').ConnectivityStatus>} */
    const connectivityStatus = ref(initialConnectivity);
    const processingLogout = ref(false);
    /**
     * What the cost eye has been set to *for this session*, or null while
     * nobody has touched it.
     *
     * Tri-state on purpose. The starting state is a configuration row, and
     * configurations arrive asynchronously — seeding a boolean at store
     * creation would read the setting before it had loaded and latch the wrong
     * answer. Null defers to the setting until the user actually clicks, and
     * their click then wins for the rest of the session.
     *
     * In-memory only — never persisted, so a page refresh or a logout goes back
     * to whatever the setting says.
     */
    const costRevealed = ref(null);

    // ----------------------------------
    // Getters (as computed properties)
    // ----------------------------------
    const user = computed(() => {
        // Safely parse
        try {
            return userData.value ? JSON.parse(userData.value) : {};
        } catch (e) {
            return {};
        }
    });

    const isLoggedIn = computed(() => {
        return !!userData.value && Object.keys(user.value).length > 0;
    });

    const getUserType = computed(() => {
        if (isLoggedIn.value) {
            return user.value.type ?? 'member';
        }
        return 'guest';
    });

    const getPermissions = computed(() => {
        return isLoggedIn.value ? permissions.value : [];
    });

    const getEmailStatus = computed(() => {
        if (isLoggedIn.value) {
            return user.value.email_verified_at ? 'verified' : 'not_verified';
        }
        return 'not_verified';
    });

    const getStatus = computed(() => {
        if (isLoggedIn.value) {
            return user.value.status ?? 'blocked';
        }
        return 'blocked';
    });

    const is_active = computed(() => {
        return isLoggedIn.value && getStatus.value === 'active';
    });

    const has_verified_email = computed(() => {
        return isLoggedIn.value && getEmailStatus.value === 'verified';
    });

    const is_admin = computed(() => {
        return isLoggedIn.value && getUserType.value === 'admin';
    });

    const is_member = computed(() => {
        return isLoggedIn.value && getUserType.value === 'member';
    });

    const is_guest = computed(() => {
        return isLoggedIn.value && getUserType.value === 'guest';
    });

    const getOnlineStatus = computed(() => {
        return connectivityStatus.value === 'online' && isSystemOnline.value;
    });

    const getConnectivityStatus = computed(() => connectivityStatus.value);

    // ----------------------------------
    // Actions
    // ----------------------------------
    const CSRF_UPDATING = (val) => {
        isUpdatingCSRF.value = val;
    };

    /**
     * Route a status change through the service rather than writing state here.
     *
     * Writing `connectivityState` directly made this a second, silent writer:
     * the raw setter notifies nobody, so a status set here never reached the
     * offline→online listeners, and its three-value whitelist would have
     * silently swallowed `backend-unavailable` and `expired-session`. The store
     * is a mirror — the mirror below repaints it when the service confirms.
     */
    const SET_CONNECTIVITY_STATUS = (status) => {
        setConnectivityStatus(status, { fromEvent: 'auth-store' });
    };

    /**
     * Mirror only the boolean. It must not invent a status: mapping `false` to
     * `'offline'` would erase a `backend-unavailable` or `expired-session`
     * verdict the probe had just established, and would tell the operator the
     * network is down when their session simply needs a sign-in.
     */
    const UPDATE_SYSTEM_ONLINE_STATUS = (val) => {
        isSystemOnline.value = Boolean(val);
    };

    /**
     * @param {object}  [opts]
     * @param {boolean} [opts.notifyOtherTabs]
     * @param {string}  [opts.reason]
     * @param {boolean} [opts.keepCachedData]
     *   Drop the credential and the logical identity, but leave the cached
     *   business data and its owner marker in place. For a credential that has
     *   simply aged out rather than an identity that has changed — see the
     *   expired-token branch in FETCH_USER_DATA. The cache stays owned by the
     *   same user id, so the existing owner checks still clear it the moment a
     *   *different* identity is verified.
     */
    const clearLocalSession = async ({
        notifyOtherTabs = true,
        reason = 'session_cleared',
        keepCachedData = false,
    } = {}) => {
        let cacheCleared = true;
        // The in-memory reference-GET memos are scoped to the departing user
        // (branch list, permission set) and must go with them, whichever path
        // ends the session — an expired token reaches here without passing
        // through the interceptor's own logout.
        clearApiGetCache();
        const previousOwnerId = user.value?.id ?? getOfflineCacheOwnerId();
        // Invalidate direct detail/refund/stock requests before any awaited
        // cleanup so a late old-user response can never repopulate the cache.
        const generation = invalidateOfflineCacheWrites();
        // Remove ownership synchronously. New guarded requests in this or
        // another tab cannot acquire a write-capable snapshot during cleanup.
        setOfflineCacheOwnerId(null);
        if (notifyOtherTabs) {
            publishOfflineSessionInvalidation({
                generation,
                ownerId: previousOwnerId,
                reason,
            });
        }
        // Drop the logical identity first so mounted watchers cannot start new
        // old-user sync work while the cache drains.
        CLEAR_USER_DATA();
        try {
            await cancelOfflineListSyncs();
        } catch (error) {
            cacheCleared = false;
            console.warn('[auth] failed to cancel offline list syncs', error);
        }
        try {
            await leaveOfflineSync();
        } catch (error) {
            cacheCleared = false;
            console.warn('[auth] failed to drain realtime cache writes', error);
        }
        if (keepCachedData) {
            // The credential aged out; the data did not change hands. Keep the
            // rows and the owner marker, drop only the identity.
            CLEAR_USER_DATA();
            setOfflineCacheOwnerId(previousOwnerId ?? null);
            return true;
        }
        // Cancel/flush the debounced POS draft writer before clearing Dexie, or
        // it can recreate the previous user's draft after logout.
        try {
            await useSaleStore().clearAll();
        } catch (error) {
            cacheCleared = false;
            console.warn('[auth] failed to clear in-memory sale state', error);
        }
        try {
            await clearAllIndexedDB();
        } catch (error) {
            cacheCleared = false;
            console.error('[auth] failed to clear offline cache', error);
        } finally {
            // A storage failure must never leave the app logically signed in.
            CLEAR_USER_DATA();
        }
        if (cacheCleared) {
            setOfflineCacheOwnerId(null);
        } else if (previousOwnerId != null) {
            // Preserve the old owner marker so the next verified identity must
            // retry the failed clear instead of adopting potentially old data.
            setOfflineCacheOwnerId(previousOwnerId);
        }
        return cacheCleared;
    };

    const FETCH_USER_DATA = async (force = false) => {
        if (!userDataFetched.value || force) {
            try {
                let fetchFromRemote = true;
                let clearToken = false;
                let cacheOwnerId = getOfflineCacheOwnerId() ?? user.value?.id ?? null;
                const offlineSession = await getOfflineSession();
                const raw = offlineSession?.token ?? null;
                if (raw) {
                    const publicKey = await importSPAPublicKey(PUBLIC_PEM);
                    const { valid, payload } = await verifyJWT(raw, publicKey);

                    if (valid && payload?.user && payload.exp * 1000 > Date.now()) {
                        const tokenUserId = payload.user.id;

                        // Do not coalesce these identities: a matching token
                        // row must not hide a conflicting cache-owner marker or
                        // previously persisted user object.
                        const identityMismatch = hasOfflineIdentityMismatch({
                            tokenUserId,
                            offlineSessionUserId: offlineSession.user_id,
                            cacheOwnerId: getOfflineCacheOwnerId(),
                            storedUserRaw: localStorage.getItem('userData'),
                            currentUserId: user.value?.id,
                        });

                        if (identityMismatch) {
                            const cleared = await clearLocalSession();
                            if (!cleared) {
                                throw new Error('Could not safely clear the previous user’s offline data.');
                            }
                            await setOfflineToken(raw, offlineSession.expires_at, tokenUserId);
                        }
                        cacheOwnerId = tokenUserId;
                        // Keep this device's appearance prefs when hydrating from
                        // the offline JWT (token is only refreshed on login/fetch).
                        let jwtUser = payload.user;
                        const existingRaw = localStorage.getItem('userData');
                        if (existingRaw) {
                            try {
                                const existing = JSON.parse(existingRaw);
                                if (existing?.id != null && String(existing.id) === String(payload.user.id)) {
                                    jwtUser = { ...payload.user };
                                    if (existing.sidebar === 'open' || existing.sidebar === 'close') {
                                        jwtUser.sidebar = existing.sidebar;
                                    }
                                    if (existing.mode === 'light' || existing.mode === 'dark') {
                                        jwtUser.mode = existing.mode;
                                    }
                                }
                            } catch (e) {
                                // Fall through with JWT payload as-is
                            }
                        }
                        userData.value = JSON.stringify(jwtUser);
                        permissions.value = payload.user.scopes || [];
                        localStorage.setItem('userData', userData.value);
                        setOfflineCacheOwnerId(payload.user.id);
                        userDataFetched.value = true;
                        fetchFromRemote = false;
                    } else {
                        clearToken = true;
                    }
                } else {
                    clearToken = true;
                }

                if (clearToken) {
                    /*
                     * An aged-out offline credential is not a change of identity.
                     *
                     * generateOfflineToken mints a 24-hour JWT while the server
                     * session lives for SESSION_LIFETIME (30 days here). Treating
                     * its expiry as a session teardown meant that once a day —
                     * usually on the first load of a shift — every terminal wiped
                     * its entire offline cache, discarded the POS draft, and
                     * broadcast a logout to every other tab, for a session the
                     * server still considered perfectly valid. The operator saw
                     * an app that had "forgotten everything overnight" and a long
                     * cold reseed before they could sell anything.
                     *
                     * Keep the rows and the owner marker; renew the credential.
                     * If a *different* user then verifies, the cacheOwnerId check
                     * below still clears the cache before adopting their data.
                     */
                    const cleared = await clearLocalSession({
                        keepCachedData: true,
                        notifyOtherTabs: false,
                        reason: 'offline_token_expired',
                    });
                    if (!cleared) {
                        throw new Error('Could not safely clear expired offline data.');
                    }
                }

                if (getOnlineStatus.value && (fetchFromRemote || force)) {
                    const response = await apiClient.get('/auth/user');
                    const user = response.data.userData;
                    if (!user?.id) throw new Error('Invalid authenticated-user response.');

                    if (cacheOwnerId != null && String(cacheOwnerId) !== String(user.id)) {
                        const cleared = await clearLocalSession();
                        if (!cleared) {
                            throw new Error('Could not safely clear the previous user’s offline data.');
                        }
                    }
                    userData.value = JSON.stringify(user);
                    permissions.value = user.scopes || [];
                    userDataFetched.value = true;
                    localStorage.setItem('userData', userData.value);
                    setOfflineCacheOwnerId(user.id);

                    // Apply account appearance from the server (cross-device / login)
                    if (user.mode === 'dark' || user.mode === 'light') {
                        useAppearance().updateAppearance(user.mode);
                    }

                    await setOfflineToken(response.data.offline_token, response.data.expires_at, user.id);
                }
            } catch (error) {
                await useHttpErrorManager().handleError(error, false);
                userDataFetched.value = false;
                // userData is seeded from localStorage at store construction, so a
                // failed verification leaves a half-signed-in shell behind: isLoggedIn
                // stays true while permissions is still empty. The router guard then
                // denies every gated route and bounces to the dashboard forever
                // instead of the login page. Drop the unverified identity so the guard
                // sees a guest. Only while online — offline the cached identity is what
                // keeps read-only access working, and a network fault is not a logout.
                if (getOnlineStatus.value && permissions.value.length === 0) {
                    CLEAR_USER_DATA();
                }
            }
        }
    };

    const CLEAR_USER_DATA = ({ removePersisted = true } = {}) => {
        userData.value = null;
        permissions.value = [];
        userDataFetched.value = false;
        // Back to following the setting, not to a hard "hidden": the next user
        // to sign in on this browser gets the installation's own default.
        costRevealed.value = null;
        if (removePersisted) localStorage.removeItem('userData');
        try {
            useHelpStore().resetLocal();
        } catch {
            /* store may be unavailable during early boot */
        }
    };

    let remoteInvalidationInProgress = false;
    const stopOfflineSessionListener = subscribeOfflineSessionInvalidations(async () => {
        if (remoteInvalidationInProgress) return;
        remoteInvalidationInProgress = true;

        // The originating tab owns the shared-storage clear. This tab only
        // invalidates local work/state, preventing a second shared clear from
        // racing a new verified login in the origin tab.
        invalidateOfflineCacheWrites({ persist: false });
        CLEAR_USER_DATA({ removePersisted: false });

        try {
            // Do not clear shared IndexedDB here: the originating tab owns that
            // operation. Only discard this tab's identity-bound Pinia state and
            // cancel its delayed draft writer.
            useSaleStore().resetLocalSessionState();
        } catch (error) {
            console.warn('[auth] failed to clear remote-tab sale state', error);
        }

        try {
            await Promise.allSettled([
                cancelOfflineListSyncs(),
                leaveOfflineSync(),
            ]);
        } catch (error) {
            console.warn('[auth] failed to stop remote-tab offline work', error);
        } finally {
            processingLogout.value = false;
            remoteInvalidationInProgress = false;
            if (router.currentRoute.value?.name !== 'login') {
                await router.replace({ name: 'login' }).catch(() => {});
            }
        }
    });

    if (import.meta.hot) {
        import.meta.hot.dispose(stopOfflineSessionListener);
    }

    // Mirror authoritative connectivity into Pinia for UI (v-online, OfflineNotice).
    // onlineCheckService must not import this store — that cycle forced dynamic imports.
    const stopConnectivityMirror = onConnectivityStatusChange((status) => {
        connectivityStatus.value = status;
        isSystemOnline.value = status === 'online';
    });
    if (import.meta.hot) {
        import.meta.hot.dispose(stopConnectivityMirror);
    }

    const processLogout = async () => {
        if (processingLogout.value) return;
        processingLogout.value = true;
        try {
            if (getOnlineStatus.value) {
                // skipAuthLogout: a 401 here means the server session is already
                // gone — do not let the axios interceptor re-enter processLogout.
                await apiClient.post('/auth/logout', null, { skipAuthLogout: true });
            }
        } catch (error) {
            // 401/419 on logout are expected for expired sessions; still clear locally.
            if (![401, 419].includes(Number(error?.response?.status))) {
                await useHttpErrorManager().handleError(error, true);
            }
        } finally {
            // Local credentials and cached business data must be removed even
            // when the remote logout request times out or the session expired.
            await clearLocalSession();
            processingLogout.value = false;
            if (router.currentRoute.value?.name !== 'login') {
                await router.push({ name: 'login' });
            }
        }
    };

    const showVerificationEmailNotice = (heading, message, id) => {
        useToast().displayToast({
            heading,
            message,
            duration: 0,
            buttons: [
                {
                    text: 'Resend Verification Email',
                    variant: 'default',
                    callback: async () => {
                        await resendVerificationEmail(id);
                    },
                },
            ],
        });
    };

    const resendVerificationEmail = async (userId) => {
        await apiClient
            .get('/sanctum/csrf-cookie', { baseURL: '/' })
            .then(async () => {
                try {
                    const response = await apiClient.post('auth/email/resend', { id: userId });
                    useToast().displayToast({
                        heading: 'Verification Email Sent',
                        message: response.data.message,
                        variant: 'success',
                    });
                } catch (error) {
                    const errors = await useHttpErrorManager().handleError(error, false);
                    useToast().displayToast({
                        heading: 'Error',
                        message: errors.full_message,
                        variant: 'error',
                    });
                }
            })
            .catch((error) => {
                useHttpErrorManager().handleError(error, true);
            });
    };

    const canAccessRoute = (route) => {
        const { accessTypes: types, accessPermissions: perms, accessCondition: cond, accessIds: ids } = route.meta ?? {};
        return checkAccess({ types, permissions: perms, ids, condition: cond });
    };

    const hasAccess = ({ ids = [], roles = [], permissions: perms = [], condition = 'OR' } = {}) => {
        return checkAccess({ types: roles, permissions: perms, ids, condition });
    };

    const canViewCost = () => hasAccess({ permissions: ['view_cost_access'] });

    /**
     * The installation's starting answer, read live so the screen corrects
     * itself the moment configurations land.
     *
     * Guarded like the help store below it: this can be reached before Pinia is
     * active during early boot, and a masked cost is the safe way to be wrong.
     */
    const costRevealDefault = () => {
        try {
            return useConfigurationStore().costRevealDefaultVisible;
        } catch {
            return false;
        }
    };

    const isCostVisible = () => canViewCost() && (costRevealed.value ?? costRevealDefault());

    // Flips what is on screen, not the stored ref — the first click on a screen
    // that opened revealed by configuration has to hide it, and `!null` is true.
    const toggleCostReveal = () => {
        if (!canViewCost()) return;
        costRevealed.value = !isCostVisible();
    };

    const hideCostReveal = () => {
        costRevealed.value = false;
    };

    const checkAccess = ({ types = [], permissions: perms = [], ids = [], condition = 'OR' }) => {
        const userType = getUserType.value;
        const userPerms = getPermissions.value;
        const userId = user.value?.id;

        // No access requirements
        if (!types.length && !perms.length && !ids.length) {
            return true;
        }

        // Public check
        if (userType === 'guest') {
            const typeAllowed = types.length === 0 || types.includes('guest');
            const permAllowed = perms.length === 0 || perms.includes('guest');
            return typeAllowed && permAllowed;
        }

        // Must be active and have verified email
        if (!(isLoggedIn.value && is_active.value && has_verified_email.value)) {
            if (isLoggedIn.value) {
                processLogout().then((r) => r); // Ensure logout if partially invalid
            }
            return false;
        }

        // Check IDs if provided
        if (ids.length > 0 && !ids.includes(userId)) {
            return false;
        }

        // Check types
        const userTypeMatches = types.length === 0 || types.includes('authenticated') || types.includes(userType);

        // Check permissions
        const hasRequiredPermission = perms.length === 0 || userPerms.some((perm) => perms.includes(perm));

        // Combine via condition
        if (types.length && perms.length) {
            return condition.toUpperCase() === 'AND' ? userTypeMatches && hasRequiredPermission : userTypeMatches || hasRequiredPermission;
        }
        return types.length ? userTypeMatches : hasRequiredPermission;
    };

    // Return everything we want to expose from this store
    return {
        // State
        userData,
        permissions,
        userDataFetched,
        isUpdatingCSRF,
        isSystemOnline,
        connectivityStatus,
        processingLogout,
        costRevealed,

        // Computed
        user,
        isLoggedIn,
        is_active,
        has_verified_email,
        is_admin,
        is_member,
        is_guest,
        getUserType,
        getPermissions,
        getEmailStatus,
        getStatus,
        getOnlineStatus,
        getConnectivityStatus,

        // Actions
        CSRF_UPDATING,
        SET_CONNECTIVITY_STATUS,
        UPDATE_SYSTEM_ONLINE_STATUS,
        FETCH_USER_DATA,
        CLEAR_USER_DATA,
        processLogout,
        showVerificationEmailNotice,
        resendVerificationEmail,
        canAccessRoute,
        hasAccess,
        canViewCost,
        isCostVisible,
        toggleCostReveal,
        hideCostReveal,
        checkAccess,
    };
});
